import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useTranslation } from "react-i18next";

interface Activity {
  id: string;
  user_id: string;
  gym_id: string;
  action: string;
  target: string;
  target_type?: string;
  target_id?: string;
  target_role?: string;
  full_name?: string;
  created_at: string;
  class_name?: string;
  booking_date?: string;
  class_time?: string;
  booking_class_id?: string;
}

interface ActivityDetailsModalProps {
  activity: Activity | null;
  open: boolean;
  onClose: () => void;
  gym?: { name: string; city: string };
}

export function ActivityDetailsModal({
  activity,
  open,
  onClose,
  gym,
}: ActivityDetailsModalProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  if (!activity) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("activityDetails.title")}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">{t("activityDetails.details")}</TabsTrigger>
            <TabsTrigger value="user">{t("activityDetails.user")}</TabsTrigger>
            <TabsTrigger value="context">{t("activityDetails.context")}</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.activityId")}</label>
                <p className="font-mono text-sm">{activity.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.date")}</label>
                <p>{format(new Date(activity.created_at), "dd MMMM yyyy 'à' HH:mm:ss", { locale: fr })}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.action")}</label>
                <p>
                  <Badge>{activity.action.replace(/_/g, " ")}</Badge>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.tenant")}</label>
                <p>{gym ? `${gym.name} - ${gym.city}` : activity.gym_id}</p>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.target")}</label>
                <p className="break-words">{activity.target}</p>
              </div>
              {activity.target_type && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.targetType")}</label>
                  <p>{activity.target_type}</p>
                </div>
              )}
              {activity.target_id && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.targetId")}</label>
                  <p className="font-mono text-sm">{activity.target_id}</p>
                </div>
              )}
              {activity.target_role && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.targetRole")}</label>
                  <p>
                    <Badge variant="outline">{activity.target_role}</Badge>
                  </p>
                </div>
              )}
            </div>

            {activity.class_name && (
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <h4 className="font-medium">{t("activityDetails.classInfo")}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t("activityDetails.name")}:</span> {activity.class_name}
                  </div>
                  {activity.booking_date && (
                    <div>
                      <span className="text-muted-foreground">{t("activityDetails.date")}:</span>{" "}
                      {format(new Date(activity.booking_date), "dd/MM/yyyy", { locale: fr })}
                    </div>
                  )}
                  {activity.class_time && (
                    <div>
                      <span className="text-muted-foreground">{t("activityDetails.time")}:</span> {activity.class_time}
                    </div>
                  )}
                  {activity.booking_class_id && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">{t("activityDetails.bookingId")}:</span>{" "}
                      <code className="text-xs">{activity.booking_class_id}</code>
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="user" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.userId")}</label>
                <p className="font-mono text-sm break-all">{activity.user_id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t("activityDetails.fullName")}</label>
                <p>{activity.full_name || t("activityDetails.notApplicable")}</p>
              </div>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                {t("activityDetails.userInfoNote")}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="context" className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">{t("activityDetails.rawData")}</label>
              <pre className="p-4 bg-muted rounded-lg text-xs overflow-x-auto">
                {JSON.stringify(activity, null, 2)}
              </pre>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
